/**
 * SVG icons for credit card brands
 */

import type { CardBrand } from '@/lib/billing/types';

interface CardBrandIconProps {
  brand: CardBrand;
  className?: string;
}

// Simple SVG icons for card brands
const brandIcons: Record<CardBrand, React.ReactNode> = {
  visa: (
    <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <rect width='24' height='24' rx='3' fill='#1A1F71' />
      <path d='M9.5 15.5L11 8.5H13L11.5 15.5H9.5Z' fill='#FFFFFF' />
      <path
        d='M17 8.5L15.3 13.3L15 11.5L14.3 8.9C14.2 8.6 13.9 8.5 13.6 8.5H11L11 8.6C11.5 8.7 12 8.9 12.4 9.1C12.6 9.2 12.7 9.4 12.8 9.6L14.5 15.5H16.5L19 8.5H17Z'
        fill='#FFFFFF'
      />
      <path
        d='M6 15.5L6.3 14H8.6L8.9 15.5H10.5L8.5 8.5H6.5L4 15.5H6ZM7.6 10.5L8.2 12.5H6.8L7.6 10.5Z'
        fill='#FFFFFF'
      />
    </svg>
  ),
  mastercard: (
    <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <rect width='24' height='24' rx='3' fill='#000000' />
      <circle cx='9' cy='12' r='5' fill='#EB001B' />
      <circle cx='15' cy='12' r='5' fill='#F79E1B' />
      <path
        d='M12 8.5C13.3 9.5 14 10.7 14 12C14 13.3 13.3 14.5 12 15.5C10.7 14.5 10 13.3 10 12C10 10.7 10.7 9.5 12 8.5Z'
        fill='#FF5F00'
      />
    </svg>
  ),
  amex: (
    <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <rect width='24' height='24' rx='3' fill='#006FCF' />
      <path
        d='M4 10L5.5 14H6.5L8 10V14H9.5V8.5H7L6 11.5L5 8.5H2.5V14H4V10Z'
        fill='#FFFFFF'
      />
      <path
        d='M10 14H14.5V12.5H11.5V11.5H14.5V10H11.5V9.5H14.5V8H10V14Z'
        fill='#FFFFFF'
      />
      <path
        d='M15.5 14H17.5L18.5 12.5L19.5 14H21.5L19.5 11L21.5 8H19.5L18.5 9.5L17.5 8H15.5L17.5 11L15.5 14Z'
        fill='#FFFFFF'
      />
    </svg>
  ),
  discover: (
    <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <rect width='24' height='24' rx='3' fill='#FFFFFF' />
      <rect x='0.5' y='0.5' width='23' height='23' rx='2.5' stroke='#E5E5E5' />
      <path
        d='M2 12H10C10 14.2 11.8 16 14 16C16.2 16 18 14.2 18 12H22V16C22 17.1 21.1 18 20 18H4C2.9 18 2 17.1 2 16V12Z'
        fill='#F47216'
      />
      <circle cx='14' cy='12' r='3' fill='#F47216' />
      <text x='4' y='10' fill='#000000' fontSize='4' fontWeight='bold'>
        DISCOVER
      </text>
    </svg>
  ),
  diners: (
    <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <rect width='24' height='24' rx='3' fill='#0079BE' />
      <circle
        cx='12'
        cy='12'
        r='6'
        stroke='#FFFFFF'
        strokeWidth='1.5'
        fill='none'
      />
      <path d='M9 9V15M15 9V15' stroke='#FFFFFF' strokeWidth='1.5' />
    </svg>
  ),
  jcb: (
    <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <rect width='24' height='24' rx='3' fill='#FFFFFF' />
      <rect x='0.5' y='0.5' width='23' height='23' rx='2.5' stroke='#E5E5E5' />
      <rect x='3' y='6' width='6' height='12' rx='1' fill='#0E4C96' />
      <rect x='9' y='6' width='6' height='12' rx='1' fill='#E71A26' />
      <rect x='15' y='6' width='6' height='12' rx='1' fill='#009A44' />
    </svg>
  ),
  unionpay: (
    <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <rect width='24' height='24' rx='3' fill='#E21836' />
      <path d='M6 6H12L10 18H4L6 6Z' fill='#00447C' />
      <path d='M10 6H16L14 18H8L10 6Z' fill='#00447C' opacity='0.8' />
      <path d='M14 6H20L18 18H12L14 6Z' fill='#00A6DE' />
    </svg>
  ),
  unknown: (
    <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <rect width='24' height='24' rx='3' fill='#6B7280' />
      <rect
        x='4'
        y='8'
        width='16'
        height='3'
        rx='1'
        fill='#FFFFFF'
        opacity='0.6'
      />
      <rect
        x='4'
        y='14'
        width='8'
        height='2'
        rx='1'
        fill='#FFFFFF'
        opacity='0.4'
      />
    </svg>
  ),
};

export function CardBrandIcon({ brand, className }: CardBrandIconProps) {
  const icon = brandIcons[brand] || brandIcons.unknown;

  return (
    <div
      className={className || 'w-10 h-6'}
      role='img'
      aria-label={`${brand} card`}
    >
      {icon}
    </div>
  );
}
